import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactPhoneV1,
  deleteContactPhoneV1,
  updateContactPhoneV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactPhone = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactPhoneV1>[0]) =>
      createContactPhoneV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldPhones = oldData.data.phones;
          if (data.isPrimary) {
            oldPhones.forEach((phone) => {
              if (phone.id !== data.id) {
                phone.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              phones: orderBy([...oldPhones, data], ["isPrimary"], ["desc"]),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating contact phone");
    },

    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact phone created successfully");
      }
    },
  });
};

export const useUpdateContactPhone = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactPhoneV1>[0]) =>
      updateContactPhoneV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldPhones = oldData.data.phones;
          if (data.isPrimary) {
            oldPhones.forEach((phone) => {
              if (phone.id !== data.id) {
                phone.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              phones: orderBy(
                oldPhones.map((phone) => {
                  return phone.id === data.id ? data : phone;
                }),
                ["isPrimary"],
                ["desc"],
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating contact phone");
    },
    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact phone updated successfully");
      }
    },
  });
};

export const useDeleteContactPhone = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactPhoneV1>[0]) =>
      deleteContactPhoneV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              phones: oldData.data.phones.filter((phone) => {
                return phone.id !== data.id;
              }),
              ...data,
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact phone");
    },
    onSettled: async () => {
      if (options?.invalidateOnSettled !== false) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact phone deleted successfully");
      }
    },
  });
};
